import { memo, useEffect, useState } from 'react'
import { Ico } from '@/ui/Ico'

interface Props {
  /** The committed filter value. An external reset pulls the box with it. */
  value: string
  onCommit: (v: string) => void
}

/**
 * The table's free-text search, deliberately its own component.
 *
 * The draft has to live somewhere, and holding it in TableView meant every
 * keystroke re-rendered the component that builds one element per lead. At
 * YLLW's 1400 leads that cost about half a second per character, which reads as
 * the tab having frozen. Keeping the draft down here means typing re-renders an
 * input and nothing else; the table only hears about it when the value settles.
 *
 * `onCommit` must be referentially stable or the memo is pointless — see
 * `setFilter` in useTableState.
 */
function TableSearch({ value, onCommit }: Props) {
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (draft === value) return
    const t = setTimeout(() => onCommit(draft), 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  return (
    <div className="tt-search">
      {Ico.search}
      <input
        type="text"
        placeholder="Sök företag, kontakt, org.nr, e-post, telefon…"
        value={draft}
        onChange={e => setDraft(e.target.value)}
      />
      {draft && (
        <button
          type="button"
          className="tt-search-clear"
          onClick={() => setDraft('')}
          aria-label="Rensa sökning"
        >×</button>
      )}
    </div>
  )
}

export default memo(TableSearch)
